import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Layers, ChevronLeft, ChevronRight, Paperclip, Type, Minus, Plus, Trash2 } from 'lucide-react';
import api from './axiosConfig';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import AttachmentsModal from './AttachmentsModal';
import AuditHistoryModal from './AuditHistoryModal';
import { useFontSize } from './FontSizeContext'; // استخدام سياق الخط اللي عندك
import { usePermissions } from './usePermissions';

const AssetCategoryDetails = () => {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { fontSize, changeFontSize } = useFontSize();
  
  const [category, setCategory] = useState({
    category_id: '', description: '', depreciation_method: 'STRAIGHT_LINE',
    useful_life_years: 5, salvage_value_percent: 10, org_id: ''
  });
  
  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
  const [showDocs, setShowDocs] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [attachCount, setAttachCount] = useState(0);
  const { canCreate, canUpdate, canDelete } = usePermissions('asset-categories');

  const loadData = useCallback(async () => {
    if (isNew){
        setCategory(prev => ({ ...prev, category_id: '', description: '' }));
        return;
    } 
    try {
      const res = await api.get(`/asset-categories-by-id/${id}`);
      setCategory(res.data);
      fetchAttachCount(res.data.category_id);
      const navRes = await api.get(`/asset-categories/${id}/navigation`);
      setNav(navRes.data);
    } catch (err) { toast.error("Error loading category"); }
  }, [id, isNew]);

  const fetchAttachCount = async (cid) => {
    const res = await api.get(`/attachments/count/CATEGORY/${cid}`);
    setAttachCount(res.data.count || 0);
  };

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const openLog = () => setShowAudit(true);
    window.addEventListener('GLOBAL_LOG_HANDLER', openLog);
    return () => window.removeEventListener('GLOBAL_LOG_HANDLER', openLog);
  }, []);

  const handleSave = async () => {
    if(!category.category_id) return toast.error("Category ID is required");
    const lid = toast.loading("Saving Category Template...");
    try {
      const res = await api.post('/asset-categories/save', category);
      toast.success("Template Saved!", { id: lid });
      if (isNew) navigate(`/asset-categories/${res.data.id}`);
    } catch (err) { toast.error("Save Failed", { id: lid }); }
  };

  const handleDelete = async () => {
  if (isNew) return;

  const result = await Swal.fire({
    title: 'Are you sure?',
    text: `You are about to delete category ${category.category_id}. This cannot be undone!`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, delete it!',
    customClass: { popup: 'font-sans text-[12px] font-bold' }
  });

  if (result.isConfirmed) {
    const lid = toast.loading("Deleting Category...");
    try {
      await api.delete(`/asset-categories/${id}`);
      toast.success("Category deleted successfully", { id: lid });
      navigate('/asset-categories'); // نرجع للقائمة بعد الحذف
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed", { id: lid });
    }
  }
};

  useEffect(() => {
    const triggerDelete = () => handleDelete();
    window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
  }, [handleDelete]);

  const goTo = (tid) => tid && navigate(`/asset-categories/${tid}`);

  return (
    <div className="flex flex-col h-screen bg-surface-app font-sans transition-[font-size] duration-300" style={{ fontSize: `${fontSize}px` }}>
      {/* Professional Toolbar */}
      <div className="bg-primary-dark text-white p-2 flex justify-between items-center px-4 shadow-lg z-30">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/asset-categories')} className="p-1.5 hover:bg-white/10 rounded-full transition-all"><ArrowLeft size={18} /></button>
          {!isNew && (
            <div className="flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1">
              <button onClick={() => goTo(nav.first)} className="p-1 hover:text-status-warning disabled:opacity-20"><ChevronLeft size={16} strokeWidth={3}/></button>
              <button onClick={() => goTo(nav.prev)} className="p-1 hover:text-status-warning disabled:opacity-20"><ChevronLeft size={16}/></button>
              <div className="px-3 text-[10px] font-mono border-x border-white/10"><span className="text-status-warning font-black">{nav.current_pos}</span> / {nav.total}</div>
              <button onClick={() => goTo(nav.next)} className="p-1 hover:text-status-warning disabled:opacity-20"><ChevronRight size={16}/></button>
              <button onClick={() => goTo(nav.last)} className="p-1 hover:text-status-warning disabled:opacity-20"><ChevronRight size={16} strokeWidth={3}/></button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowDocs(true)} className="relative p-2 hover:bg-white/10 rounded transition-all">
            <Paperclip size={18} className="text-primary" />
            {attachCount > 0 && <span className="absolute -top-1 -right-1 bg-status-danger text-[8px] w-4 h-4 rounded-full flex items-center justify-center font-black animate-pulse">{attachCount}</span>}
          </button>
          <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20">
            <button onClick={() => changeFontSize(-1)}><Minus size={14} /></button>
            <Type size={14} /> <span className="text-[10px] font-black">{fontSize}</span>
            <button onClick={() => changeFontSize(1)}><Plus size={14} /></button>
          </div>
          {((isNew && canCreate) || (!isNew && canUpdate)) && (
          <button onClick={handleSave} className="bg-status-success hover:bg-status-success px-6 py-1.5 rounded-sm font-black text-[11px] flex items-center gap-2 uppercase shadow-md transition-all">
            <Save size={14}/> Save Template
          </button>
          )}

          {!isNew && canDelete && (
            <button
              onClick={handleDelete}
              className="p-2 hover:bg-status-danger/20 rounded text-status-danger transition-colors"
              title="Delete Category"
              disabled={isNew}
            >
                <Trash2 size={16}/>
            </button>
          )}
        </div>
      </div>

      {/* Content Area */}
      <div className="p-8 max-w-5xl mx-auto w-full space-y-6 overflow-y-auto custom-scrollbar">
        <div className="bg-surface-card p-8 rounded-sm shadow-sm border-t-4 border-primary grid grid-cols-3 gap-10">
          <div className="flex flex-col gap-1 border-b border-line-subtle pb-2">
            <label className="text-[10px] font-black text-status-danger uppercase tracking-widest">Category Code *</label>
            <input className="text-lg font-black text-primary outline-none uppercase"
                   value={category.category_id} onChange={e => setCategory({...category, category_id: e.target.value.toUpperCase()})} />
          </div>
          <div className="col-span-2 flex flex-col gap-1 border-b border-line-subtle pb-2">
            <label className="text-[10px] font-black text-ink-secondary uppercase tracking-widest">Global Description</label>
            <input className="text-lg font-bold text-ink outline-none"
                   value={category.description} onChange={e => setCategory({...category, description: e.target.value})} />
          </div>
        </div>

        <div className="bg-surface-card p-8 rounded-sm shadow-sm border grid grid-cols-3 gap-12">
            <div className="col-span-3 border-b pb-2 flex items-center gap-2 text-primary font-black text-xs uppercase tracking-widest">
                <Layers size={16} className="text-status-warning" /> Depreciation Rules (قواعد الإهلاك التلقائي)
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[10px] font-black text-ink-secondary uppercase">Method</label>
              <select className="border-b-2 border-line-subtle font-bold text-primary outline-none py-1 cursor-pointer focus:border-status-warning transition-all"
                      value={category.depreciation_method} onChange={e => setCategory({...category, depreciation_method: e.target.value})}>
                <option value="STRAIGHT_LINE">Straight Line (قسط ثابت)</option>
                <option value="DOUBLE_DECLINING">Double Declining (رصيد متناقص)</option>
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[10px] font-black text-ink-secondary uppercase">Life Expectancy (Years)</label>
              <input type="number" className="border-b-2 border-line-subtle font-bold text-primary outline-none py-1 focus:border-status-warning transition-all"
                     value={category.useful_life_years} onChange={e => setCategory({...category, useful_life_years: e.target.value})} />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-[10px] font-black text-ink-secondary uppercase">Salvage Value (%)</label>
              <input type="number" className="border-b-2 border-line-subtle font-bold text-primary outline-none py-1 focus:border-status-warning transition-all"
                     value={category.salvage_value_percent} onChange={e => setCategory({...category, salvage_value_percent: e.target.value})} />
            </div>
        </div>
      </div>

      <AuditHistoryModal isOpen={showAudit} onClose={() => setShowAudit(false)} tableName="asset_categories" recordId={category.category_id} />

      {showDocs && (
        <AttachmentsModal isOpen={showDocs} onClose={() => {setShowDocs(false); fetchAttachCount(category.category_id);}}
                          ownertable="CATEGORY" ownerid={category.category_id} />
      )}
    </div>
  );
};

export default AssetCategoryDetails;